import React, { useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import { Justify, Button } from 'tea-component';
import { getCountryFlag } from '@/pages/home/_config/countries';
import SearchRecent from '@intelligence/pages/GenreProfile/components/Search/SearchRecent';
import { LANGUAGE_CN } from '@/pages/home/_config/config';

export const TABLE_SEARCH_PLACEHOLDERS: {
  [key: string]: string;
} = {
  games: 'dbi_genre_profile_search_games',
  genres: 'dbi_genre_profile_search_genre',
  companies: 'dbi_genre_profile_search_companies',
};

/**
 * 品类画像搜索组件
 * @param props 组件属性
 * @param props.marketType 市场类型
 * @param props.dimension 维度类型(games/companies)
 * @param props.getPageParams 获取页面参数方法
 * @param props.searchApi 搜索接口
 * @param props.pageLogInfo 页面日志信息
 * @param props.renderTableData 渲染表格数据方法
 * @param props.getTableData 获取表格数据方法
 * @param props.resetOuterTableData 重置外部表格数据方法
 * @param props.genreName 品类名称
 * @param props.loadingDispatch loading状态派发方法
 * @param props.tableActionDispatch 表格action派发方法
 * @returns 搜索组件
 */
export default function GenreProfileSearch(props) {
  const {
    pageConfig,
    marketType,
    dimension,
    getPageParams,
    searchApi,
    pageLogInfo,
    renderTableData,
    getTableData,
    resetOuterTableData, // 清空时还原表格
    genreName, // 品类名 判断接口传参用
    loadingDispatch,
    tableActionDispatch,
    getSearchListFront,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const searchType = useMemo(() => `market_stats_${marketType}_${dimension}`, [marketType, dimension]);
  const keyMap = useMemo(() => pageConfig?.keyMap(), [pageConfig]);

  const isFrontSearch = useMemo(() => dimension === 'genres', [dimension]);

  const getItemRawName = (item: any) => {
    return item?.[keyMap?.gametitle] || item?.[keyMap?.id] || '';
  };

  const getItemDisplayName = (item: any) => {
    const rawName = getItemRawName(item);
    const cnName = item?.[keyMap?.gametitleCn];

    if (locale === LANGUAGE_CN) {
      return cnName || rawName;
    }

    return rawName || cnName;
  };

  // 将表格接口返回的数据转换为组件内部需要的数据格式
  const transTableDataForSearch = (res: any) => {
    const list = isFrontSearch ? getSearchListFront(res) : res?.data?.list || {};
    return list?.map((item) => {
      const name = getItemDisplayName(item);
      if (isFrontSearch) return {
        ...item,
        name,
      };
      const countryImg = getCountryFlag(item[keyMap?.abbr]);
      const publisher = item[keyMap?.publisher];
      const country = item[keyMap?.country];
      const cover = item[keyMap?.cover];
      return {
        ...item,
        imgSrc: cover,
        lineOneStr: name,
        lineTwoImg: countryImg,
        lineTwoStr: `${publisher},${country}`,
      };
    });
  };

  // 将搜索记录的接口返回数据转换为组件内部需要的数据格式
  const transRecordDataForSearch = (res: any) => {
    const result = res?.data || [];
    return result?.map((item) => ({
      ...item,
      imgSrc: item.metric_cover_url,
      // 搜索的时候将值展示在input上
      name: item.metric_name,
      lineOneStr: item.metric_name,
      lineTwoImg: getCountryFlag(item.country_abbre),
      lineTwoStr: item.company_name,
    }));
  };

  // 将接口返回的搜索结果（表格数据）转换为添加搜索记录接口所需的数据格式
  const getAddRecordParams = (item) => {
    const name = getItemDisplayName(item);
    const rawName = getItemRawName(item);
    if (isFrontSearch) {
      return {
        metric_id: rawName,
        metric_name: name,
        type: searchType,
      };
    }
    const countryImg = item[keyMap?.abbr];
    const publisher = item[keyMap?.publisher];
    const country = item[keyMap?.country];
    const cover = item[keyMap?.cover];
    const id = item[keyMap?.id];
    return {
      city_name: country,
      company_name: publisher,
      country_abbre: countryImg,
      metric_cover_url: cover,
      metric_id: id,
      metric_name: name,
      type: searchType,
    };
  };

  // 将组件返回的数据转换为删除记录接口所需的数据格式
  const getDeleteRecordParams = (item) => ({
    name: item.metric_name,
  });

  // 调用设置表格的方法，将搜索结果展示在前端
  const setTableSearchResult = (item, type) => {
    // metric_id是搜索点击时传过来的id
    // keyMap?.id是数据接口返回的id
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    if (isFrontSearch && type !== 'record') {
      renderTableData({
        tableList: [item],
      }, true, true);
    } else {
      const addParams = isFrontSearch ? {
        genres: [item?.metric_id || item[keyMap?.id]]
      } : {
        select_code: [item?.metric_id || item[keyMap?.id]]
      };
      getTableData({
        modifiedParams: {
          page: 1,
          ...addParams
        },
        isOuter: true,
      });
    }
  };

  const resetTableData = () => {
    resetOuterTableData();
  };
  const tablePlaceholder = useMemo(() => {
    const searchPlaceholderLocaleKey =
      TABLE_SEARCH_PLACEHOLDERS[dimension] ?? 'dbi_label_search';
    return formatMessage({ id: searchPlaceholderLocaleKey });
  }, [locale, dimension]);

  return (
    <Justify
      className="dbi-card-justify"
      right={
        <>
          <SearchRecent
            key={searchType}
            searchType={searchType}
            isOuterSearchKey={true}
            dataNameKey={'metric_id'}
            fetchParams={getPageParams}
            searchListApi={searchApi}
            pageLogInfo={pageLogInfo}
            transTableDataForSearch={transTableDataForSearch}
            transRecordDataForSearch={transRecordDataForSearch}
            getTableResultList={setTableSearchResult}
            handleClear={resetTableData}
            placeholder={tablePlaceholder}
            isCustomRendering={!!isFrontSearch}
            isFrontEndSearch={!!isFrontSearch}
            getAddRecordParams={getAddRecordParams}
            getDeleteRecordParams={getDeleteRecordParams}
            genreName={genreName}
            dimension={dimension}
            loadingDispatch={loadingDispatch}
          />
        </>
      }
    />
  );
}
